import camelCase from 'lodash/camelCase'
import castArray from 'lodash/castArray'
import find from 'lodash/find'
import forEach from 'lodash/forEach'
import map from 'lodash/map'
import mapValues from 'lodash/mapValues'
import reduce from 'lodash/reduce'
import replace from 'lodash/replace'
import sortBy from 'lodash/sortBy'
import split from 'lodash/split'
import startsWith from 'lodash/startsWith'
import toInteger from 'lodash/toInteger'
import uniq from 'lodash/uniq'
import uniqBy from 'lodash/uniqBy'
import { parseAsArrayOf, parseAsInteger, parseAsString, parseAsStringEnum } from 'nuqs'

import { ProductListBlockFacetType, ProductListBlockFragmentFragment } from '@Generated/graphql'
import { FacetDynamicConfig, FacetStats, NumericFilter, ProductSorting } from '@Globals/Algolia/Types'

type QueryParametersRaw = Record<string, string | string[] | undefined>
type QueryParameters = Record<string, string[]>

export function parseHitsPerPage(limit: number | null | undefined, usePagination: boolean): number {
  if (limit && Number.isInteger(limit) && limit > 0) {
    return limit
  }

  return usePagination ? 60 : 1000
}

export const normalizeQueryParameters = (query: QueryParametersRaw): QueryParameters => mapValues(
  query,
  value => uniq(
    reduce(
      castArray(value),
      (valueMemo, item) => {
        if (!item) {
          return valueMemo
        }

        return [...valueMemo, ...item.split(',')]
      },
      [],
    ),
  ),
)

const parseValidFacetFilters = (
  query: QueryParameters,
  config: ProductListBlockFragmentFragment['facetConfig'],
): string[] => sortBy(
  reduce(
    query,
    (memo, values, key) => {
      if (!startsWith(key, 'filter_')) {
        return memo
      }

      const shortKey = camelCase(replace(key, /^filter_/, ''))
      const facet = find(config, ['key', shortKey])

      if (!facet
        || !facet.id
        || (facet.type !== ProductListBlockFacetType.EnumerableDefault
          && facet.type !== ProductListBlockFacetType.EnumerableBoolean
          && facet.type !== ProductListBlockFacetType.EnumerableColor
          && facet.type !== ProductListBlockFacetType.EnumerableNumeric
        )
      ) {
        return memo
      }

      // Only add values that exist for the given facet
      return reduce(
        values,
        (memoInner, valueKey) => {
          // "__" is only group syntax when the facet has groups; free-text
          // values (looks ambassador handles) may contain it literally.
          if (valueKey.includes('__') && facet.groups && facet.groups.length !== 0) {
            const [groupKey] = split(valueKey, '__')
            const facetGroup = find(facet.groups, ['id', groupKey])
            if (!facetGroup) {
              return memoInner
            }

            const facetValue = find(facetGroup.values, ['id', valueKey])
            if (!facetValue) {
              return memoInner
            }

            return [...memoInner, `${facet.id}:${facetValue.id}`]
          }

          const facetValue = find(facet.values, ['id', valueKey])
          if (!facetValue) {
            return memoInner
          }

          return [...memoInner, `${facet.id}:${facetValue.id}`]
        },
        memo,
      )
    },
    [],
  ),
)

const parseValidNumericFilters = (
  query: QueryParameters,
  config: ProductListBlockFragmentFragment['facetConfig'],
  facetStats: FacetStats,
) => reduce<QueryParameters, Record<string, NumericFilter>>(
  query,
  (memo, values, key) => {
    if (!startsWith(key, 'filter_')) {
      return memo
    }

    const shortKey = camelCase(replace(key, /^filter_/, ''))
    const facet = find(config, ['key', shortKey])
    const stats = facetStats[facet?.id || '']

    if (values.length === 1) {
      values.push(values[0])
    }

    if (!facet
      || !facet.id
      || (facet.type !== ProductListBlockFacetType.RangeDefault && facet.type !== ProductListBlockFacetType.RangePrice)
      || values.length !== 2
      || !stats
    ) {
      return memo
    }

    const [minRaw, maxRaw] = values
    const min = Math.max(parseInt(minRaw, 10), 0)
    const max = Math.min(parseInt(maxRaw, 10), stats.max)

    if (min > max || (min < stats.min && max > stats.max)) {
      return memo
    }

    return {
      ...memo,
      [facet.id]: {
        max,
        min,
      },
    }
  },
  {},
)

export const parseValidPage = (query: QueryParameters): number => {
  if (!query.page || query.page.length !== 1) {
    return 1
  }

  const page = toInteger(query.page[0])

  if (page < 1 || page > 1000) {
    return 1
  }

  return page
}

const parseValidSorting = (query: QueryParameters): ProductSorting => {
  if (!query.sorting || query.sorting.length !== 1) {
    return ProductSorting.Default
  }

  const sorting = query.sorting[0] as ProductSorting

  if (!Object.values(ProductSorting).includes(sorting)) {
    return ProductSorting.Default
  }

  return sorting
}

export const parseValidQueryParameters = (
  query: QueryParametersRaw,
  config: ProductListBlockFragmentFragment['facetConfig'],
  facetStats: FacetStats,
) => {
  const normalizedQuery = normalizeQueryParameters(query)

  return {
    facetFilters: parseValidFacetFilters(normalizedQuery, config),
    numericFilters: parseValidNumericFilters(normalizedQuery, config, facetStats),
    sorting: parseValidSorting(normalizedQuery),
    page: parseValidPage(normalizedQuery),
  }
}

// countAppliedFilters counts active filter_* query states. Shared by the
// products and looks search providers.
export function countAppliedFilters(queryStates: Record<string, unknown>): number {
  return reduce(
    queryStates,
    (memo, item, key) => {
      if (!item || !key.startsWith('filter_')) {
        return memo
      }

      if (Array.isArray(item)) {
        return memo + item.length
      }

      return memo + 1
    },
    0,
  )
}

// computeFacetDynamicConfig derives the per-facet checked/count/disabled state
// from the current filters and the Algolia facet response. Shared by the
// products and looks search providers.
export function computeFacetDynamicConfig(
  facetConfig: ProductListBlockFragmentFragment['facetConfig'],
  facetFilters: string[],
  numericFilters: Record<string, NumericFilter>,
  facets: Record<string, Record<string, number>> | undefined,
  facetsStats: FacetStats | undefined,
): Record<string, FacetDynamicConfig> {
  return reduce(facetConfig, (memo, facet) => { /* eslint-disable no-param-reassign */
    if (facet.type === ProductListBlockFacetType.EnumerableDefault
      || facet.type === ProductListBlockFacetType.EnumerableBoolean
      || facet.type === ProductListBlockFacetType.EnumerableColor
      || facet.type === ProductListBlockFacetType.EnumerableNumeric
    ) {
      let checkedCount = 0
      let groupCount = 0

      forEach(facet.values, (value) => {
        const checked = facetFilters.includes(`${facet.id}:${value.id}`)
        const count = facets?.[facet.id!]?.[value.id!] || 0

        if (checked) {
          checkedCount += 1
        }

        memo[`${facet.parameter}:${value.id}`] = {
          checked,
          count,
          disabled: count === 0,
          key: value.key || '',
          name: value.name || null,
          type: 'value',
        }
      })

      forEach(facet.groups, (group) => {
        forEach(group.values, (value) => {
          const checked = facetFilters.includes(`${facet.id}:${value.id}`)
          const count = facets?.[facet.id!]?.[value.id!] || 0

          if (checked) {
            checkedCount += 1
          }

          memo[`${facet.parameter}:${value.id}`] = {
            checked,
            count,
            disabled: count === 0,
            key: value.key || '',
            name: value.name || null,
            type: 'value',
          }
        })

        groupCount += 1
      })

      memo[facet.parameter!] = {
        count: checkedCount,
        disabled: false,
        name: facet.name || null,
        showGroupTitle: groupCount > 1 && facet.key !== 'colorShades',
        type: 'facet',
      }
    }

    if (facet.type === ProductListBlockFacetType.RangeDefault || facet.type === ProductListBlockFacetType.RangePrice) {
      const numericFilter = numericFilters?.[facet.id!]
      memo[facet.parameter!] = {
        count: numericFilter ? 1 : 0,
        disabled: !!facetsStats?.[facet.id!],
        name: facet.name || null,
        showGroupTitle: false,
        type: 'facet',
      }
    }

    return memo
  }, {} as Record<string, FacetDynamicConfig>)
}

export function processURLSearchParams(search: URLSearchParams) {
  search.sort()
  return search
}
export const queryParameterEnumerable = parseAsArrayOf(parseAsString)
export const queryParameterRange = parseAsArrayOf(parseAsInteger)
export const queryParameterPage = parseAsInteger.withDefault(1)
export const queryParameterSorting = parseAsStringEnum<ProductSorting>(Object.values(ProductSorting)).withDefault(ProductSorting.Default)

export function createQueryStatesKeys(facetConfig: ProductListBlockFragmentFragment['facetConfig']) {
  return Object.fromEntries(
    sortBy(
      uniqBy(
        [
          ['page', queryParameterPage],
          ['sorting', queryParameterSorting],
          ['q', parseAsString],
          ...map(facetConfig, (facet) => {
            if (facet.type === ProductListBlockFacetType.RangeDefault || facet.type === ProductListBlockFacetType.RangePrice) {
              return [facet.parameter, queryParameterRange]
            }
            return [facet.parameter, queryParameterEnumerable]
          }),
        ],
        '0',
      ),
      0,
    ),
  )
}
